/**
 * Inventory limits + object catalog API. Same access pattern as
 * room_definitions: read for all staff, write Admin-only via RLS.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import type { InventoryLimit, ObjectCatalogEntry } from '../domain/types';
import type { Database } from '../lib/database.types';

type ObjectCatalogRow = Database['public']['Tables']['object_catalog']['Row'];
type InventoryLimitRow = Database['public']['Tables']['inventory_limits']['Row'];

function rowToCatalogEntry(row: ObjectCatalogRow): ObjectCatalogEntry {
  return {
    itemType: row.item_type,
    displayName: row.display_name,
    category: row.category,
    defaultSeats: row.default_seats,
    maxSeats: row.max_seats,
    defaultWidthFt: row.default_width_ft,
    defaultHeightFt: row.default_height_ft,
    iconKey: row.icon_key,
    hasStockLimit: row.has_stock_limit,
  };
}

function rowToInventoryLimit(row: InventoryLimitRow): InventoryLimit {
  return {
    itemType: row.item_type,
    maxQuantity: row.max_quantity,
  };
}

const OBJECT_CATALOG_KEY = ['object-catalog'] as const;
const INVENTORY_LIMITS_KEY = ['inventory-limits'] as const;
const CHAIR_STOCK_KEY = ['chair-stock'] as const;

export function useObjectCatalog() {
  return useQuery({
    queryKey: OBJECT_CATALOG_KEY,
    queryFn: async (): Promise<ObjectCatalogEntry[]> => {
      const { data, error } = await supabase.from('object_catalog').select('*');
      if (error) throw error;
      return data.map(rowToCatalogEntry);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useInventoryLimits() {
  return useQuery({
    queryKey: INVENTORY_LIMITS_KEY,
    queryFn: async (): Promise<InventoryLimit[]> => {
      const { data, error } = await supabase.from('inventory_limits').select('*');
      if (error) throw error;
      return data.map(rowToInventoryLimit);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useChairStock() {
  return useQuery({
    queryKey: CHAIR_STOCK_KEY,
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase.from('chair_stock').select('quantity').single();
      if (error) throw error;
      return data.quantity;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Admin-only — enforced by RLS. */
export function useUpdateObjectCatalogEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: Partial<ObjectCatalogEntry> & { itemType: ObjectCatalogEntry['itemType'] }
    ): Promise<ObjectCatalogEntry> => {
      const { itemType, ...rest } = input;
      const { data, error } = await supabase
        .from('object_catalog')
        .update({
          ...(rest.displayName !== undefined && { display_name: rest.displayName }),
          ...(rest.defaultSeats !== undefined && { default_seats: rest.defaultSeats }),
          ...(rest.maxSeats !== undefined && { max_seats: rest.maxSeats }),
          ...(rest.defaultWidthFt !== undefined && { default_width_ft: rest.defaultWidthFt }),
          ...(rest.defaultHeightFt !== undefined && { default_height_ft: rest.defaultHeightFt }),
        })
        .eq('item_type', itemType)
        .select()
        .single();

      if (error) throw error;
      return rowToCatalogEntry(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: OBJECT_CATALOG_KEY });
    },
  });
}

/** Admin-only — enforced by RLS. */
export function useUpdateInventoryLimit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: InventoryLimit): Promise<InventoryLimit> => {
      const { data, error } = await supabase
        .from('inventory_limits')
        .update({ max_quantity: input.maxQuantity })
        .eq('item_type', input.itemType)
        .select()
        .single();

      if (error) throw error;
      return rowToInventoryLimit(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVENTORY_LIMITS_KEY });
    },
  });
}

/** Admin-only — enforced by RLS. */
export function useUpdateChairStock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (quantity: number): Promise<number> => {
      const { data, error } = await supabase
        .from('chair_stock')
        .update({ quantity })
        .eq('id', true)
        .select('quantity')
        .single();

      if (error) throw error;
      return data.quantity;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHAIR_STOCK_KEY });
    },
  });
}
